// BOORO: the team of a space. The agents as a tree, each with its state and
// its numbers, and the place an agent works at.
import { oc } from "@orpc/contract";
import * as z from "zod";
import { Id } from "./ids.js";

/** The state an agent shows in the team view. */
export const AGENT_LIGHTS = ["busy", "waiting", "problem", "stuck", "idle"] as const;
export type AgentLight = (typeof AGENT_LIGHTS)[number];

export const TeamAgentSchema = z.object({
  id: Id,
  name: z.string(),
  title: z.string(),
  description: z.string(),
  color: z.string(),
  /** The agent this one works under. */
  parentBotId: Id.nullable(),
  /** The state the team view shows. */
  light: z.enum(AGENT_LIGHTS),
  /** The run status of the agent. */
  status: z.string(),
  /** How many tasks are open for the agent. */
  openTasks: z.number().int().nonnegative(),
  /** How well the agent did, null when it did nothing. */
  successRate: z.number().min(0).max(1).nullable(),
  /** Whether the agent is the Chief of the space. */
  isChief: z.boolean(),
  /** Whether the agent has its own computer. */
  hasOwnComputer: z.boolean(),
  /** Whether the agent asked for its own computer, and why. */
  needsOwnComputer: z.boolean(),
  computerReason: z.string().nullable(),
  /** The agents that work on the same computer. */
  sharedWith: z.array(z.string()),
  /** The computer the agent works on; null for the Chief, which has none. */
  computerId: z.string().nullable(),
  /** How deep the agent sits in the tree. */
  depth: z.number().int().nonnegative(),
  hasChildren: z.boolean(),
});
export type TeamAgent = z.infer<typeof TeamAgentSchema>;

export const SetComputerNeedInput = z.object({
  botId: Id,
  /** True when the agent needs a computer of its own. */
  needsOwnComputer: z.boolean(),
  /** Why the agent needs one; shown next to the switch. */
  reason: z.string().max(200).optional(),
});
export type SetComputerNeedInput = z.infer<typeof SetComputerNeedInput>;

export const booroTeamContract = {
  /** The agents of the person, as a tree. */
  agents: oc.output(z.array(TeamAgentSchema)),
  /** Notes that an agent needs a computer of its own, and why. */
  setComputerNeed: oc.input(SetComputerNeedInput).output(z.object({ ok: z.literal(true) })),
};
